import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { StylovyzeComposableDialog, Button, Text } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';

interface DeleteCostTableDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  costTableName: string;
  isLoading: boolean;
  isCheckingInUse?: boolean;
  isInUse?: boolean;
}

export const DeleteCostTableDialog: React.FC<DeleteCostTableDialogProps> = ({
  open,
  onClose,
  onConfirm,
  costTableName,
  isLoading = false,
  isCheckingInUse = false,
  isInUse = false,
}) => {
  const { t } = useGlobalization();

  const renderBody = () => {
    if (isCheckingInUse) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (isInUse) {
      return (
        <Text variant="text-large">
          {t(
            'Cannot delete this cost table because it is currently used by one or more Rehab Actions.'
          )}
        </Text>
      );
    }
    return (
      <Text variant="text-large">
        {t(
          'Are you sure you want to delete the cost table: {{costTableName}}',
          { costTableName }
        )}
      </Text>
    );
  };

  const renderFooter = () => {
    if (isCheckingInUse) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
          <Button
            onClick={onClose}
            color="secondary"
            variant="outlined"
            disabled>
            {t('Cancel')}
          </Button>
          <Button color="primary" variant="contained" disabled>
            {t('Delete')}
          </Button>
        </Box>
      );
    }
    if (isInUse) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
          <Button onClick={onClose} color="secondary" variant="outlined">
            {t('Close')}
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={isLoading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}>
          {t('Delete')}
        </Button>
      </Box>
    );
  };

  return (
    <StylovyzeComposableDialog
      open={open}
      onCancel={onClose}
      title={t('Delete Cost Table')}
      width="500px"
      dialogProps={{
        sx: {
          '& .MuiDialog-paper': {
            maxWidth: { xs: '90vw', sm: '500px' },
            width: '100%',
            margin: { xs: 1, sm: 3 },
          },
        },
      }}
      footer={renderFooter()}>
      {renderBody()}
    </StylovyzeComposableDialog>
  );
};

export default DeleteCostTableDialog;
